import { useState } from 'react';

import type {
  AdminLedgerAllowance,
  AdminLedgerRow,
  AdminLedgerSummary,
  LedgerKind,
} from '@/lib/adminApi';
import { useUserLedger } from '@/lib/useAdminUsers';

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/** Credits as a person reads them: whole numbers stay whole, fractions keep two places. */
function formatCredits(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function signed(value: number): string {
  return value > 0 ? `+${formatCredits(value)}` : formatCredits(value);
}

const KIND_LABEL: Record<LedgerKind, string> = {
  agent_turn: 'Agent turn',
  node_run: 'Generation',
  run: 'Run charge',
  dictation: 'Dictation',
  other_charge: 'Other charge',
  refund: 'Refund',
  purchase: 'Purchase',
  purchase_reversal: 'Purchase reversed',
  grant: 'Grant',
  adjustment: 'Adjustment',
  other: 'Other',
};

/**
 * The Coin ledger tab of a user's card (support ticket 28.09.2026).
 *
 * WHY IT EXISTS. The Content tab lists RUNS, and the bill is not only runs:
 * an agent turn is charged per assistant message with no run at all, and
 * deleting a workflow deletes its runs while the ledger keeps the charge. The
 * ticket's user had 324 credits charged for 18 generations under a Content
 * tab that said "No content yet" — which reads as "unused, refund in full".
 * This tab is the ledger itself: every debit and credit, named, with the
 * headline sums over the WHOLE ledger (the API computes them, not this page).
 *
 * Every admin role sees it (tokens and history as ROWS are support's, the
 * owner's line 869f4c8v4). It shows ids and numbers only — a chat id is text
 * here, not a link, because opening a chat is the superadmin's alone.
 */
export function UserLedgerTab({ userId }: { userId: string }) {
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  const { data, isLoading, isError } = useUserLedger(userId, cursor);

  if (isLoading) return <p className="text-body text-fg-hint">Loading…</p>;
  if (isError || !data) {
    return <p className="text-body text-destructive">Could not load the coin ledger. Try again.</p>;
  }

  // Every row names its organization (Codex review of !694): the API spans
  // every organization the person is in, and a debit that cannot be tied to
  // a plan cannot be judged. The name comes from the allowance list; the id
  // is the fallback, never a blank.
  const orgNames: Record<string, string | null> = Object.fromEntries(
    (data.allowances ?? []).map((a) => [a.organization_id, a.organization_name]),
  );

  return (
    <div>
      <Allowances allowances={data.allowances ?? []} />
      <LedgerSummary summary={data.summary} />
      <p className="mt-3 text-micro text-fg-tertiary">
        Credits are held by the organization, so this is the organization&apos;s ledger; rows made by
        a teammate are marked. Agent turns have no run, and a generation whose workflow was deleted
        keeps its charge here after its run is gone from Content.
      </p>

      {data.rows.length === 0 ? (
        <p className="mt-4 text-body text-fg-hint">No coin movements yet.</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-card border border-border">
          <table className="w-full text-left text-body">
            <thead>
              <tr className="border-b border-border bg-surface-muted text-meta text-fg-secondary">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2 text-right">Credits</th>
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Model</th>
                <th className="px-3 py-2">Organization</th>
                <th className="px-3 py-2">By</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <LedgerRow key={row.id} row={row} orgNames={orgNames} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={cursorStack.length <= 1}
          onClick={() => setCursorStack((s) => s.slice(0, -1))}
          className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={!data.next}
          onClick={() => setCursorStack((s) => [...s, data.next ?? null])}
          className="rounded-card border border-border px-3 py-1 text-meta disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function LedgerSummary({ summary }: { summary: AdminLedgerSummary }) {
  const tiles: { label: string; value: number; hint?: string; id?: string }[] = [
    { label: 'Spent on agent', value: summary.spent_agent },
    { label: 'Spent on generations', value: summary.spent_generations },
    { label: 'Other spend', value: summary.spent_other, hint: 'dictation and unlinked charges' },
    { label: 'Refunded', value: summary.refunded },
    { label: 'Net spent', value: summary.net_spent, hint: 'spent minus refunded' },
    // Gross, reversed and net SIDE BY SIDE (Codex review of !694): a refunded
    // pack is a negative purchase row, and gross alone on a refund-decision
    // screen hides that the money already went back.
    {
      id: 'packs-bought',
      label: 'Packs bought',
      value: summary.purchased,
      hint: 'gross; not the plan allowance',
    },
    {
      id: 'packs-reversed',
      label: 'Packs reversed',
      value: summary.purchase_reversed,
      hint: 'refunded or charged back',
    },
    {
      id: 'packs-net',
      label: 'Packs net',
      value: summary.purchased - summary.purchase_reversed,
      hint: 'bought minus reversed',
    },
    { label: 'Granted', value: summary.granted, hint: 'admin grants and sign-up gifts' },
    { label: 'Waived', value: summary.waived, hint: 'complimentary, not charged' },
  ];
  return (
    <div
      data-testid="ledger-summary"
      className="grid grid-cols-2 gap-3 sm:grid-cols-4"
    >
      {tiles.map((t) => (
        <div
          key={t.label}
          data-testid={t.id ? `ledger-tile-${t.id}` : undefined}
          className="rounded-card border border-border bg-surface-field p-3"
        >
          <div className="text-micro text-fg-secondary">{t.label}</div>
          <div className="mt-1 text-body-lg font-medium text-fg-primary">{formatCredits(t.value)}</div>
          {t.hint && <div className="mt-1 text-micro text-fg-tertiary">{t.hint}</div>}
        </div>
      ))}
    </div>
  );
}

function formatDay(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/**
 * The plan allowance, per member organization. It is NOT in the ledger — a
 * subscription's credits are the plan's limit for the period — so without this
 * line a paying user's ledger reads "bought 0" beside real spend.
 */
function Allowances({ allowances }: { allowances: AdminLedgerAllowance[] }) {
  if (allowances.length === 0) return null;
  return (
    <div className="mb-3 flex flex-col gap-2">
      {allowances.map(({ organization_id, organization_name, balance }) => (
        <div
          key={organization_id}
          data-testid="ledger-allowance"
          className="rounded-card border border-border bg-surface-muted px-3 py-2 text-meta text-fg-secondary"
        >
          <div className="text-micro text-fg-tertiary">
            {organization_name ?? 'Organization'}{' '}
            <span className="break-all font-mono">{organization_id}</span>
          </div>
          {balance === null ? (
            <span>Balance could not be computed for this organization.</span>
          ) : balance.unlimited ? (
            <span>
              Plan allowance: unlimited · used this period {formatCredits(balance.used)}
            </span>
          ) : (
            <span>
              Plan allowance {formatCredits(balance.allowance ?? 0)} for{' '}
              {formatDay(balance.period.start)} – {formatDay(balance.period.end)} · used{' '}
              {formatCredits(balance.used)} · packs {formatCredits(balance.purchased)} · remaining{' '}
              <span className="font-medium text-fg-primary">{formatCredits(balance.remaining ?? 0)}</span>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function Id({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-micro text-fg-tertiary">
      {label} <span className="break-all font-mono text-fg-secondary">{value}</span>
    </div>
  );
}

function LedgerRow({
  row,
  orgNames,
}: {
  row: AdminLedgerRow;
  orgNames: Record<string, string | null>;
}) {
  const orgName = row.organization_id ? orgNames[row.organization_id] : null;
  const tone =
    row.credits < 0
      ? 'text-destructive-soft-foreground'
      : row.credits > 0
        ? 'text-success-soft-foreground'
        : 'text-fg-secondary';
  return (
    <tr data-testid="ledger-row" className="border-b border-border align-top last:border-0">
      <td className="px-3 py-2 text-fg-secondary">{formatDate(row.date_created)}</td>
      <td className="px-3 py-2 text-fg-primary">
        {KIND_LABEL[row.kind] ?? row.kind}
        {row.waived_credits !== null && row.waived_credits > 0 && (
          <div className="text-micro text-fg-tertiary">
            complimentary, {formatCredits(row.waived_credits)} waived
          </div>
        )}
      </td>
      <td className={`px-3 py-2 text-right font-medium ${tone}`}>{signed(row.credits)}</td>
      <td className="px-3 py-2">
        {row.pipeline_run_id && (
          <Id
            label={row.run_exists === false ? 'Run (deleted)' : 'Run'}
            value={row.pipeline_run_id}
          />
        )}
        {row.node_run_id && <Id label="Node run" value={row.node_run_id} />}
        {row.chat_id && <Id label="Chat" value={row.chat_id} />}
        {!row.chat_id && row.message_id && <Id label="Message" value={row.message_id} />}
        {!row.pipeline_run_id && !row.node_run_id && !row.chat_id && !row.message_id && (
          <span className="text-micro text-fg-tertiary">{row.source ?? '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 text-micro text-fg-secondary">{row.model_key ?? '—'}</td>
      <td className="px-3 py-2 text-micro text-fg-secondary">
        {orgName ?? (
          <span className="break-all font-mono">{row.organization_id ?? '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 text-micro text-fg-secondary">
        {row.profile_id === null ? 'Organization' : row.by_this_user ? 'This user' : 'Teammate'}
      </td>
    </tr>
  );
}
